import type { EvalQuestion } from '@/types/domain';

/* 5-point Likert scale, 1 = Strongly disagree / least, 5 = Strongly agree / most.
   A course's evaluation satisfies its `eval` certificate requirement. Templates are
   per-course (see EVAL_BY_COURSE) and fall back to the generic set below. */

const DEFAULT_EVAL: EvalQuestion[] = [
  { id: 'q1', prompt: { en: 'The course objectives were clearly explained.', th: 'วัตถุประสงค์ของหลักสูตรชัดเจน' } },
  { id: 'q2', prompt: { en: 'The content was relevant to my clinical practice.', th: 'เนื้อหาเกี่ยวข้องกับงานของฉัน' } },
  { id: 'q3', prompt: { en: 'The instructor explained concepts effectively.', th: 'ผู้สอนอธิบายเนื้อหาได้ดี' } },
  { id: 'q4', prompt: { en: 'The assessment fairly reflected the material.', th: 'แบบทดสอบสอดคล้องกับเนื้อหา' } },
  { id: 'q5', prompt: { en: 'I feel confident applying what I learned.', th: 'ฉันมั่นใจที่จะนำไปใช้' } },
];

/* ACLS post-seminar survey — provided by the ED (Thai-primary). */
const ACLS_EVAL: EvalQuestion[] = [
  { id: 'q1', prompt: { en: 'The ACLS content was clear and easy to understand.', th: 'เนื้อหาการอบรม ACLS มีความชัดเจนและเข้าใจง่าย' } },
  { id: 'q2', prompt: { en: 'The instructor explained the steps of emergency patient care systematically.', th: 'วิทยากรอธิบายขั้นตอนการดูแลผู้ป่วยฉุกเฉินได้เป็นระบบ' } },
  { id: 'q3', prompt: { en: 'The training increased my confidence in assessing and managing cardiac-arrest patients.', th: 'การอบรมช่วยเพิ่มความมั่นใจในการประเมินและดูแลผู้ป่วย cardiac arrest' } },
  { id: 'q4', prompt: { en: 'The hands-on practice / simulation deepened my understanding of ACLS.', th: 'การฝึกปฏิบัติ/สถานการณ์จำลองช่วยให้เข้าใจ ACLS มากขึ้น' } },
  { id: 'q5', prompt: { en: 'The duration of the training was appropriate for the content.', th: 'ระยะเวลาในการอบรมเหมาะสมกับเนื้อหา' } },
  { id: 'q6', prompt: { en: 'The content can be applied to my real work.', th: 'เนื้อหาที่ได้รับสามารถนำไปใช้ในการปฏิบัติงานจริงได้' } },
  { id: 'q7', prompt: { en: 'Overall, I am satisfied with this ACLS training.', th: 'โดยรวมแล้ว ท่านพึงพอใจกับการอบรม ACLS ครั้งนี้' } },
];

/* Course id → its evaluation template. */
const EVAL_BY_COURSE: Record<string, EvalQuestion[]> = {
  acls: ACLS_EVAL,
};

/** The evaluation questions for a given course (falls back to the generic template). */
export function evalQuestionsFor(courseId: string): EvalQuestion[] {
  return EVAL_BY_COURSE[courseId] ?? DEFAULT_EVAL;
}

/** Generic template — kept for non-course-scoped callers. */
export const evalQuestions = DEFAULT_EVAL;

export const LIKERT_LABELS: Record<number, { en: string; th: string }> = {
  1: { en: 'Strongly disagree', th: 'ไม่เห็นด้วยอย่างยิ่ง' },
  2: { en: 'Disagree', th: 'ไม่เห็นด้วย' },
  3: { en: 'Neutral', th: 'เฉย ๆ' },
  4: { en: 'Agree', th: 'เห็นด้วย' },
  5: { en: 'Strongly agree', th: 'เห็นด้วยอย่างยิ่ง' },
};
